/**
 * Agency feature hooks.
 *
 * useIsAgencyAdmin:
 *   Determines whether the current org is an agency AND the user is an
 *   owner or admin. The org context does NOT include `org_type` (the
 *   Organization interface only has id/name/slug), so we query
 *   public.organizations directly — same schema("public") pattern used
 *   in OrganizationContext.tsx.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useOrganization } from "@/contexts/OrganizationContext";
import { AGENCY_QUERY_KEYS } from "./api";

// ---------------------------------------------------------------------------
// useOrgType — fetch org_type for the current org
// ---------------------------------------------------------------------------

function useOrgType(orgId: string | null) {
  return useQuery({
    queryKey: AGENCY_QUERY_KEYS.orgType(orgId ?? ""),
    enabled: !!orgId,
    staleTime: 10 * 60 * 1_000, // org_type rarely changes
    queryFn: async (): Promise<"agency" | "client" | null> => {
      if (!orgId) return null;

      // org_type was added in P5-T1 migration but the generated TS types
      // haven't been regenerated yet — cast to bypass TypeScript's column check.
      const client = supabase.schema("public") as unknown as {
        from: (table: string) => {
          select: (cols: string) => {
            eq: (col: string, val: string) => {
              maybeSingle: () => Promise<{ data: unknown; error: unknown }>;
            };
          };
        };
      };

      const { data, error } = await client
        .from("organizations")
        .select("org_type")
        .eq("id", orgId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return (data as { org_type: string | null }).org_type as "agency" | "client" | null;
    },
  });
}

// ---------------------------------------------------------------------------
// useIsAgencyAdmin — public hook used by AgencyRoute and AppShell nav gating
// ---------------------------------------------------------------------------

const AGENCY_ADMIN_ROLES = ["owner", "admin"] as const;

/**
 * Returns whether the current session is an agency admin (org_type === 'agency'
 * AND role in owner/admin).
 *
 * `isLoading` is true while either the org context or the org_type query is
 * in flight — callers should render a loading state until it settles.
 */
export function useIsAgencyAdmin(): { isAgencyAdmin: boolean; isLoading: boolean } {
  const { organizationId, organizationRole, isLoading: orgLoading } = useOrganization();

  const { data: orgType, isPending: typePending } = useOrgType(
    orgLoading ? null : organizationId,
  );

  const isLoading = orgLoading || typePending;

  const isAgencyAdmin =
    !isLoading &&
    orgType === "agency" &&
    AGENCY_ADMIN_ROLES.includes(organizationRole as (typeof AGENCY_ADMIN_ROLES)[number]);

  return { isAgencyAdmin, isLoading };
}
